import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { config } from '@/constants';
import { getFirebaseIdToken, signOut } from '@/services/auth';
import { getStoredTenantId } from '@/utils/helper/tenant-storage';
import { getActiveBranchId } from '@/utils/branchStore';
import { markPageNavigatingAway } from '@/utils/helper/error-reporter';

export const serviceApiInstance: AxiosInstance = axios.create({
  baseURL: config.BASE_API_URL,
  // A request that never settles hangs every `await` above it — the auth
  // bootstrap awaits me/profile, so without a timeout a dead connection
  // (backend cold start, LINE-webview network stall) means "Loading..."
  // forever. Generous enough for a Cloud Run cold start, but finite.
  timeout: 20_000,
});

serviceApiInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const token = await getAuthToken();
    const tenantId = getTenantId();

    if (token && config.headers) {
      config.headers.set
        ? config.headers.set('authorization', token)
        : (config.headers['authorization'] = token);
    }
    if (tenantId && tenantId !== 'undefined' && config.headers) {
      config.headers.set
        ? config.headers.set('x-tenant-id', tenantId)
        : (config.headers['x-tenant-id'] = tenantId);
    }
    if (config.headers) {
      const hasBranchId = config.headers.get
        ? config.headers.get('branch-id')
        : config.headers['branch-id'];
      if (!hasBranchId) {
        // Reflects the branch selected via the LINE OA Switch Branch control
        // (LineBranchContext keeps branchStore in sync). Falls back to '1' for
        // single-branch tenants / before the branch list resolves.
        const activeBranchId = getActiveBranchId() || '1';
        config.headers.set
          ? config.headers.set('branch-id', activeBranchId)
          : (config.headers['branch-id'] = activeBranchId);
      }

      config.headers.set
        ? config.headers.set('platform', 'member')
        : (config.headers['platform'] = 'member');
    }

    return config;
  },
  (error: AxiosError): Promise<never> => {
    return Promise.reject(error);
  }
);

serviceApiInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: AxiosError): Promise<never | AxiosResponse> => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const responseData = error.response?.data as { code?: string };

    // QTY-1697: backend rejects every member-facing call from an INACTIVE
    // member with this stable code — force logout. On the login page the
    // error is surfaced in the form instead of redirecting.
    if (responseData?.code === 'MEMBER_INACTIVE') {
      if (!globalThis.location.pathname.startsWith('/login')) {
        await signOut();
        markPageNavigatingAway();
        globalThis.location.href = '/login?error=member_inactive';
      }
      throw error;
    }

    if (
      responseData?.code === 'auth/expired' &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await getFirebaseIdToken(true); // force refresh
        if (newToken && originalRequest.headers) {
          originalRequest.headers['authorization'] = `Bearer ${newToken}`;
          return serviceApiInstance(originalRequest);
        }
      } catch (refreshError) {
        await signOut();
        markPageNavigatingAway();
        globalThis.location.href = '/login';
      }
    }

    throw error;
  }
);

async function getAuthToken(): Promise<string | null> {
  try {
    const token = await getFirebaseIdToken();
    return token ? `Bearer ${token}` : null;
  } catch (error) {
    return null;
  }
}

function getTenantId(): string | null {
  // tenant-storage falls back to (and restores from) the cookie mirror when
  // the LINE webview evicted localStorage — see tenant-storage.ts.
  return getStoredTenantId();
}
